import React, { forwardRef, useRef, useState } from "react"
import "react-datepicker/dist/react-datepicker.css";
import 'react-csv-importer/dist/index.css';
import "../../../../../styles.css";
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import ErrorIcon from "@mui/icons-material/Error";
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import DatePicker from 'react-datepicker';
import { useTheme } from "@mui/styles";
import CircularProgress from "@mui/material/CircularProgress";
import {addMonths, subMonths} from 'date-fns';
import { useDispatch, useSelector } from "react-redux";
import { selectAddMortgagePaymentPerm } from "../../../../../slices/userActionsPermissionsSlice";
import { useAddPrincipalOnlyPaymentMutation, useReloadTeamPortfolioMonthlyStatsMutation } from "../../../../../slices/api/apiSlice";
import { ActionIconWrapper, Button, ButtonWrapper, ComponentBorder, ComponentHeader, ErrorWrapper, FlexWrapper, ModalContainer, StyledInputElement, Typography } from "../../../../../StyledComponents";

const AddPaymentModal = ({ handleCloseModal, mortgageFinancials, teamId, userFullName }) => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const updatedPrincipal = useRef()

  const [dateRequired, setDateRequired] = useState(null)
  const [startDate, setStartDate] = useState(new Date())
  
  const [isFetching, setIsFetching] = useState(null)
  const [isError, setIsError] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [principalRequired, setPrincipalRequired] = useState(true)
  const [principalValue, setPrincipalValue] = useState(null)
  const [principalValueStr, setPrincipalValueStr] = useState('')
  const [statusPrincipal, setStatusPrincipal] = useState(null)

  const addMortgagePaymentPerm = useSelector(selectAddMortgagePaymentPerm)

  const [addPrincipalOnlyPayment] = useAddPrincipalOnlyPaymentMutation()
  const [reloadTeamPortfolioMonthlyStats] = useReloadTeamPortfolioMonthlyStatsMutation()

  const handleSubmit = async () => {
    setIsFetching(true)
    let resp = await addPrincipalOnlyPayment({
      paymentAmount: principalValue,
      paymentDate: startDate,
      mortgageId: mortgageFinancials.mortgageId,
      teamId: teamId,
      userFullName: userFullName,
    })
    if (resp.error) {
      setIsError(true)
    } else {
      if (resp.data.message.includes('success')) {
        let reload = await reloadTeamPortfolioMonthlyStats({
          userFullName: userFullName,
          teamId: teamId,
        })
        if (reload.error) {
          setIsError(true)
        } else {
          setIsSuccess(true)
        }
      }
    }
    setIsFetching(false)
  }

  const ExampleCustomInput = forwardRef(({ value, onClick, onChange }, ref) => (
    <input
      value={value}
      className="styled-calendar-input"
      onClick={onClick}
      onChange={onChange}
      ref={ref}
    />
  ))

  return (
    <ModalContainer className="onePanel">
      <ComponentBorder className="top" style={{gridRow: '1', gridColumn: '1/4'}}>
        <ActionIconWrapper className="medium grayText circle hoverBorder hover" style={{margin: 'auto 2rem auto 0'}}>
          <CancelIcon onClick={() => handleCloseModal()} style={{height: '100%', width: '100%'}} />  
        </ActionIconWrapper>
      </ComponentBorder>
      <FlexWrapper className="column alignCenter justifyBetween">
        {isSuccess ?
          <ComponentHeader className="modal green" style={{height: '4rem', alignItems: 'center'}}>
            <FlexWrapper style={{border: `0.2rem solid ${theme.palette.primary.darkest}`, padding: '0.5rem', width: '2.7rem', background: `${theme.palette.primary.main}`}}>
              <CheckIcon style={{height: '2.5rem', width: '2.5rem', color: `${theme.palette.common.white}`}} />
            </FlexWrapper>
            <span style={{padding: '0 0 0 1rem', fontSize: '2rem'}}>
              Success
            </span>
          </ComponentHeader>
        : isError ?
          <ComponentHeader className="modal red" style={{height: '4rem', alignItems: 'center'}}>
            <FlexWrapper style={{border: `0.2rem solid ${theme.palette.common.redDark}`, padding: '0.5rem', width: '2.7rem', background: `${theme.palette.common.redBright}`}}>
              <ErrorIcon style={{height: '2.5rem', width: '2.5rem', color: `${theme.palette.common.white}`}} />
            </FlexWrapper>
            <span style={{padding: '0 0.5rem', fontSize: '1.4rem'}}>
              Failed... please try again. Reach out to support@lancastersweep.com if the problem persists.
            </span>
          </ComponentHeader>
        :
          <ComponentHeader className="modal" style={{height: '4rem', alignItems: 'center'}}>
            <Typography className="xLarge">
              Add Principal-Only Payment
            </Typography>
          </ComponentHeader>
        }
        <FlexWrapper className="fitContentHeight fitContentWidth" style={{marginTop: '2rem'}}>
          <InfoIcon style={{color: `${theme.palette.common.grayBorderDark}`, margin: '0.1rem 0.1rem 0 0'}} />
          <Typography className="small">
            Principal only payments will adjust the<br/>
            interest/principal balance for all following months.<br/>
          </Typography>
        </FlexWrapper>
        <FlexWrapper className="justifyBetween alignEnd fitContentWidth fitContentHeight">
          <FlexWrapper className='column justifyEnd  fitContentWidth' style={{marginRight: '2rem'}}>
            <Typography className="fitContentHeight" style={{marginBottom: '0.3rem'}}>
              Month Paid
            </Typography>
            <FlexWrapper className="column justifyEnd fitContentWidth" style={{height: '2.5rem'}}>
              <FlexWrapper className="alignEnd fitContentWidth" style={{height: '2.5rem'}}>
                <DatePicker
                  dropdownMode="scroll"
                  dateFormat="MMMM yyyy"
                  selected={startDate}
                  onChange={(date) => {              
                    setStartDate(date)
                    setDateRequired(false)
                    if (isSuccess || isError) {
                      setIsError(false)
                      setIsSuccess(false)
                    }
                  }}
                  maxDate={addMonths(new Date(), 12)}
                  minDate={subMonths(new Date(), 360)}
                  showMonthDropdown
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={10}
                  withPortal
                  portalId="root-portal"
                  customInput={<ExampleCustomInput />}
                  dayClassName={() => "example-datepicker-day-class"}
                  calendarClassName="example-datepicker-class"
                  todayButton="CLICK FOR TODAY'S DATE"
                  disabled={!addMortgagePaymentPerm}
                  style={{cursor: !addMortgagePaymentPerm ? 'not-allowed' : ''}}
                />
              </FlexWrapper>
            </FlexWrapper>
          </FlexWrapper>
          <FlexWrapper className="column justifyBetween alignEnd fitContentWidth" style={{height: '6rem', marginLeft: '2rem'}}>
            <FlexWrapper className='column justifyEnd alignEnd'>
              {(principalRequired && !statusPrincipal) && (
                <ErrorWrapper className="fitContentWidth">
                  <ErrorIcon style={{width: '1.2rem', height: '1.2rem'}} />
                  Required
                </ErrorWrapper>
              )}
              {statusPrincipal === "nonNumber" && (
                <ErrorWrapper>
                  <ErrorIcon style={{width: '1.2rem', height: '1.2rem'}} />
                  Must be a number
                </ErrorWrapper>
              )}
              {statusPrincipal === "negative" && (
                <ErrorWrapper>
                  <ErrorIcon style={{width: '1.2rem', height: '1.2rem'}} />
                  Can't be negative
                </ErrorWrapper>
              )}
              {statusPrincipal === "zero" && (
                <ErrorWrapper>
                  <ErrorIcon style={{width: '1.2rem', height: '1.2rem'}} />
                  Can't be zero
                </ErrorWrapper>
              )}
              {statusPrincipal === "large" && (
                <ErrorWrapper>
                  <ErrorIcon style={{width: '1.2rem', height: '1.2rem'}} />
                  Larger than 100m
                </ErrorWrapper>
              )}
              <Typography className="fitContentHeight" style={{paddingRight: '1rem', marginBottom: '0.3rem'}}>
                Payment Amount
              </Typography>
            </FlexWrapper>
            <FlexWrapper className="alignCenter fitContentWidth" style={{height: '2.5rem'}}>
              <span style={{color: `${theme.palette.primary.main}`, fontWeight: '600', padding: '0 0 0.1rem 0', margin: '0.1rem 0.2rem 0 0', fontSize: '1.2rem'}}>$</span>
              <StyledInputElement className="borderBottomOnly" 
                style={{width: '9.5rem', height: '2.5rem', padding: '0 0 0 0.3rem', cursor: !addMortgagePaymentPerm ? 'not-allowed' : ''}}
                value={principalValueStr ? principalValueStr : ""}
                disabled={!addMortgagePaymentPerm}
                type="text"
                name="principal"
                id="principal"
                tabIndex="2"
                ref={updatedPrincipal}
                onChange={() => {
                  if (isSuccess || isError) {
                    setIsError(false)
                    setIsSuccess(false)
                  }
                  if (updatedPrincipal.current.value.length === 0) {
                    setPrincipalRequired(true)
                  }
                  if (updatedPrincipal.current.value.includes('.')) {
                    setPrincipalValue((updatedPrincipal.current.value.replace(/[^0-9-.]/g, '')))
                    setPrincipalValueStr((updatedPrincipal.current.value.toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0})))
                  } else {
                    if (!/^[0-9,.]*$/.test(updatedPrincipal.current.value)) {
                      setStatusPrincipal("nonNumber")
                    } else if (Math.sign(parseFloat(updatedPrincipal.current.value)) === -1) {
                      setStatusPrincipal("negative")
                    } else if (Math.sign(parseFloat(updatedPrincipal.current.value)) === 0) {
                      setStatusPrincipal("zero")
                    } else if (parseFloat(updatedPrincipal.current.value.replace(/,/g, "")) > 100000000) {
                      setStatusPrincipal("large")
                    } else if (statusPrincipal === "nonNumber" || statusPrincipal === "negative" || statusPrincipal === "zero" || statusPrincipal === "large" || principalRequired) {
                      setStatusPrincipal(null)
                      if (updatedPrincipal.current.value.length !== 0) {
                        setPrincipalRequired(false)
                      }
                    } else {
                      setStatusPrincipal(null)
                    }
                    if ((parseFloat(updatedPrincipal.current.value.replace(/,/g, "")) < 100000000 || (isNaN(parseFloat(updatedPrincipal.current.value.replace(/,/g, ""))))) && updatedPrincipal.current.value.length < 12) {
                      if (!isNaN(parseFloat(updatedPrincipal.current.value))) {
                        setPrincipalValueStr(parseFloat(updatedPrincipal.current.value.replace(/,/g, "")).toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0}))
                      } else {
                        setPrincipalValueStr(updatedPrincipal.current.value)
                      }
                      setPrincipalValue(parseFloat(updatedPrincipal.current.value.replace(/[^0-9-.]/g, '')))
                    }
                  }
                }}
              />
            </FlexWrapper>
          </FlexWrapper>
        </FlexWrapper>  

        <ButtonWrapper style={{margin: '3rem auto 1rem auto'}}>         
          <Tooltip
            componentsProps={{tooltip: {sx: {
              color: `${theme.tooltip.error.color}`,
              backgroundColor: `${theme.tooltip.error.background}`,
              border: `0.1rem solid ${theme.tooltip.error.border}`,
              borderBottom: `0.1rem solid ${theme.tooltip.error.border}`,
              opacity: '0',
              borderRadius: '1rem',
              width: 'fit-content',
              minWidth: 'fit-content',
              padding: '0.5rem 1rem',
            }}}}
            PopperProps={{modifiers: [{
              name: "offset",
              options: {
                offset: [0, 0],
              },
            }]}}
            title={!addMortgagePaymentPerm ?
              <FlexWrapper className="column">
                <Typography className="bold alignTextCenter">
                  Elevated authority required
                </Typography>
              </FlexWrapper>
            : statusPrincipal ?
              <FlexWrapper className="column">
                <Typography className="bold alignTextCenter">
                  Form inputs required
                </Typography>
              </FlexWrapper>
            : (dateRequired || principalRequired) ?
              <FlexWrapper className="column">
                <Typography className="bold alignTextCenter">
                  Form inputs required
                </Typography>
              </FlexWrapper>
            : null} 
            placement="top"
            arrow
          >
            <Button 
              className={(!addMortgagePaymentPerm || dateRequired || principalRequired || isSuccess || isError) ? "primaryDisabled" : isFetching ? "primaryLoading" : "primary"}
              onClick={(!addMortgagePaymentPerm || dateRequired || principalRequired || isFetching || isSuccess || isError) ? null : () => handleSubmit()}
              style={{width: '24rem'}}
              tabIndex={5}
            >
              {isFetching ?
                <CircularProgress sx={{ color: `${theme.palette.common.cardBackground}` }} size={13} />
              :
                `Add Payment`
              }
            </Button>
          </Tooltip>
        </ButtonWrapper>
      </FlexWrapper>
      <ComponentBorder className="left" style={{gridRow:'2', gridColumn: '1'}}></ComponentBorder>
      <ComponentBorder className="right"  style={{gridRow:'2', gridColumn: '3'}}></ComponentBorder>
      <ComponentBorder className="bottom" style={{gridRow:'3', gridColumn: '1/4'}}></ComponentBorder>
    </ModalContainer>
  )
}

export default AddPaymentModal